// Console home / overview — the role-aware landing page (replaces the old redirect
// straight into API Docs). Greets the user, shows the current environment + a small
// "at a glance" stats strip, and offers quick-action cards filtered by role. All data
// comes from existing endpoints; failures degrade to "—" rather than breaking the page.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Tag,
  Space,
  Button,
} from "antd";
import {
  FileTextOutlined,
  ApiOutlined,
  TeamOutlined,
  MailOutlined,
  SafetyOutlined,
  BankOutlined,
  TagsOutlined,
  FileSearchOutlined,
  DatabaseOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { meApi, usersApi, invitesAdminApi, accountApi, brandsApi } from "../../console/consoleApi";
import { useSession } from "../../console/useSession";
import { useEnv } from "../../console/useEnv";
import { ROLES, ADMIN_ROLES, roleLabel } from "../../console/consoleUtils";

const { Title, Text, Paragraph } = Typography;

const ENV_LABEL = { preprod: "Preprod", prod: "Prod" };

export default function ConsoleHome() {
  const navigate = useNavigate();
  const { user } = useSession();
  const { env } = useEnv();

  const isInternal = user?.role === ROLES.INTERNAL_ADMIN;
  const isAccountAdmin = ADMIN_ROLES.includes(user?.role) && !isInternal;
  const envLabel = ENV_LABEL[env] ?? env;

  // Env-scoped stats (credits + enabled APIs) — re-fetch on env toggle.
  const [me, setMe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    meApi
      .apis(env)
      .then((res) => !cancelled && setMe(res.data.data))
      .catch(() => !cancelled && setMe({ provisioned: false }));
    return () => {
      cancelled = true;
    };
  }, [env]);

  // Identity-scoped counts (loaded once) — which ones depends on role.
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    const set = (patch) => !cancelled && setCounts((c) => ({ ...c, ...patch }));

    if (isAccountAdmin) {
      usersApi.list().then((r) => set({ team: r.data.data.users.length })).catch(() => {});
      invitesAdminApi
        .list({ openOnly: true })
        .then((r) => set({ pending: r.data.data.invites.length }))
        .catch(() => {});
    }
    if (isInternal) {
      accountApi
        .listAll()
        .then((r) => {
          const accts = r.data.data.accounts;
          set({ accounts: accts.length, activeAccounts: accts.filter((a) => a.isActive).length });
        })
        .catch(() => {});
      brandsApi.list().then((r) => set({ brands: r.data.data.brands.length })).catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [isAccountAdmin, isInternal]);

  const enabledApis = me == null ? "—" : me.provisioned ? (me.allApis ? "All" : (me.apis?.length ?? 0)) : "—";
  const credits = me == null ? "—" : me.provisioned && me.credits != null ? me.credits : "—";

  // Quick-action cards, filtered by role.
  const actions = [
    { title: "API Documentation", desc: "Browse the full API catalogue and try requests live.", icon: <FileTextOutlined />, to: "/console/docs", show: true },
    { title: "My APIs", desc: "The APIs you're enabled to call, and your credits.", icon: <ApiOutlined />, to: "/console/my-apis", show: true },
    { title: "Team", desc: "Manage your organisation's members and their access.", icon: <TeamOutlined />, to: "/console/users", show: isAccountAdmin },
    { title: "Invitations", desc: "Invite people and manage pending invites.", icon: <MailOutlined />, to: "/console/invites", show: isAccountAdmin },
    { title: "Account settings", desc: "Your organisation's name and allowed domains.", icon: <SafetyOutlined />, to: "/console/account", show: isAccountAdmin },
    { title: "Accounts", desc: "Manage client organisations and their people.", icon: <BankOutlined />, to: "/console/accounts", show: isInternal },
    { title: "Brands", desc: "Email sender identities and which account uses each.", icon: <TagsOutlined />, to: "/console/brands", show: isInternal },
    { title: "Audit log", desc: "Who changed what — actor, target, before/after.", icon: <FileSearchOutlined />, to: "/console/audit-log", show: isInternal || isAccountAdmin },
    { title: "API Collection", desc: "Add or remove APIs in the catalogue.", icon: <DatabaseOutlined />, to: "/console/tools/api-collection", show: isInternal },
  ].filter((a) => a.show);

  const greetingName = user?.name || user?.email || "there";

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Greeting */}
      <div>
        <Space align="center" wrap>
          <Title level={3} style={{ margin: 0 }}>
            Welcome, {greetingName}
          </Title>
          {user?.role && (
            <Tag color={isInternal ? "geekblue" : "default"}>{roleLabel(user.role)}</Tag>
          )}
          <Tag color={env === "prod" ? "volcano" : "blue"}>{envLabel}</Tag>
        </Space>
        <Paragraph type="secondary" style={{ margin: "8px 0 0" }}>
          This is your {user?.brand?.displayName} Console overview. Jump into any area below.
        </Paragraph>
      </div>

      {/* At a glance */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title={`Credits (${envLabel})`} value={credits} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title={`Enabled APIs (${envLabel})`} value={enabledApis} />
          </Card>
        </Col>
        {isAccountAdmin && (
          <>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic title="Team members" value={counts.team ?? "—"} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic title="Pending invites" value={counts.pending ?? "—"} />
              </Card>
            </Col>
          </>
        )}
        {isInternal && (
          <>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title="Accounts"
                  value={counts.accounts ?? "—"}
                  suffix={
                    counts.accounts != null ? (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        ({counts.activeAccounts} active)
                      </Text>
                    ) : null
                  }
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic title="Brands" value={counts.brands ?? "—"} />
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Quick actions */}
      <div>
        <Title level={5} style={{ marginBottom: 12 }}>
          Quick actions
        </Title>
        <Row gutter={[16, 16]}>
          {actions.map((a) => (
            <Col xs={24} sm={12} lg={8} key={a.to}>
              <Card hoverable onClick={() => navigate(a.to)} style={{ height: "100%" }}>
                <Space align="start">
                  <span style={{ fontSize: 22, color: "#0066ff" }}>{a.icon}</span>
                  <div>
                    <Text strong>{a.title}</Text>
                    <Paragraph type="secondary" style={{ margin: "4px 0 0" }}>
                      {a.desc}
                    </Paragraph>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* First-time hint */}
      <Card size="small" style={{ background: "#f6f9ff", borderColor: "#d6e4ff" }}>
        <Space wrap>
          <Text type="secondary">New here? Start by browsing the API docs and testing a call.</Text>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate("/console/docs")}>
            Open API Docs <ArrowRightOutlined />
          </Button>
        </Space>
      </Card>
    </Space>
  );
}
